import { ChevronDown, Loader2, X } from "lucide-react";

import useAudioStore from "@/store/useAudioStore";
import { Volume2, VolumeX } from "lucide-react";
import Image from "@/components/Image";
import { usePathname, useRouter } from "next/navigation";
import { useReward } from "@/constant/reward";
import { ModeType, Quest } from "@/constant/schema";
import useRewardStore from "@/store/useRewardStore";
import useAuthStore from "@/store/useAuthStore";
import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import { getHeaderData } from "@/lib/questService";
import useConfigStore from "@/store/useConfigStore";
import { colors } from "@/constant/colors";
import { getScheduleFromDB, Schedule } from "@/constant/schedule";
import DiamondPopup from '@/components/Popup/DiamondPopup';
import AnimatedBar from './AnimatedBar';
import Bar from './Bar';
import Combo from './Combo';
import report, { ReportEvent } from "@/lib/report";
import { useDeviceType } from "@/lib/hooks";
import { isParentProfile } from "@/lib/profile-role";

// Lazy load components that are controlled by state
const QuestPracticePopup = lazy(() => import("../Popup/quest-practice-popup").then(module => ({ default: module.QuestPracticePopup })));
const HeartAnimation = lazy(() => import("../Animation/heartAnimation"));

interface HeaderProps {
  showAllChapters: boolean;
  setShowAllChapters: (show: boolean) => void;
  currentProgress: number;
  totalDiamondImageRef: React.RefObject<HTMLImageElement | null>;
  questId: string;
  cover_image: string;
  chapter_title: string;
  lesson_title: string;
  showHeart?: boolean;
  titleRef?: React.RefObject<HTMLDivElement | null>;
  animatedBar?: boolean;
  comboCount?: number;
  from: string;
  modeType: ModeType;
  hideTableOfContents?: boolean;
  hideChapterTitle?: boolean;
  hideCoverImage?: boolean;
  previewOnly?: boolean;
  initialChapterId?: string | null;
  initialLessonId?: string | null;
  onAuthRequired?: () => void;
}

const Header = ({
  showAllChapters,
  setShowAllChapters,
  currentProgress,
  totalDiamondImageRef,
  cover_image,
  chapter_title,
  lesson_title,
  showHeart = false,
  titleRef,
  questId,
  animatedBar = false,
  comboCount = 0,
  modeType = ModeType.TEST,
  from,
  hideTableOfContents = false,
  hideChapterTitle = false,
  hideCoverImage = false,
  previewOnly = false,
  initialChapterId = null,
  initialLessonId = null,
  onAuthRequired,
}: HeaderProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const reward = useReward();
  const { user, profile } = useAuthStore();
  const { isMuted, toggleMute, interruptAudio } = useAudioStore();
  const { betterExperienceTip } = useConfigStore();
  const diamondCount = reward.diamond;
  const closeBetterExperienceTip = useConfigStore(state => state.closeBetterExperienceTip);
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const { diamondPopup, demoRewardMode } = useRewardStore();
  const showDemoGems = demoRewardMode || (!user && (pathname?.startsWith('/learn/') || pathname?.startsWith('/practice/')));
  const [quest, setQuest] = useState<Quest | null>(null);
  const [showLeavingModal, setShowLeavingModal] = useState(false);
  const allowAssignActions = !user || isParentProfile(profile);
  // 在pad端要显示窄一点的Header
  const deviceType = useDeviceType();
  const baseHeight = deviceType === 'tablet' ? 60 : 100;

  useEffect(() => {
    const fetchQuest = async () => {
      const quest = await getHeaderData(questId);
      setQuest(quest);
    }
    if (showAllChapters && questId) {
      fetchQuest();
    }
  }, [showAllChapters, questId]);

  useEffect(() => {
    if (showAllChapters) {
      interruptAudio();
    }
  }, [interruptAudio, showAllChapters]);

  useEffect(() => {
    if (user?.id && questId) {
      getScheduleFromDB(user.id, questId).then(schedule => {
        if (schedule) {
          setSchedule(schedule);
        }
      });
    }

  }, [user, questId]);

  const getBackTarget = useCallback(() => {
    if (!user && (pathname?.startsWith('/learn/') || pathname?.startsWith('/practice/'))) {
      return '/';
    }
    if (from === 'parent-assignment') {
      return '/';
    }
    if (from) {
      return '/' + from;
    }
    return null;
  }, [from, pathname, user]);

  useEffect(() => {
    const target = getBackTarget();
    if (target) {
      router.prefetch(target);
    }
  }, [getBackTarget, router]);

  const handleBack = () => {
    setShowLeavingModal(true);
    report(ReportEvent.HEADER_BACK_BUTTON, {
      from: from || '',
    });

    const target = getBackTarget();
    window.setTimeout(() => {
      if (target) {
        router.push(target);
        return;
      }
      router.back();
    }, 60);
  }

  const renderBookTitle = () => {
    return (
      <div className="flex flex-col flex-1 min-w-0 justify-between items-center overflow-hidden md:overflow-visible pl-0 md:pl-10">
        <div className="flex flex-col w-full overflow-hidden md:overflow-visible">
          <div className="flex flex-row w-full items-center justify-center">
            <button
              onClick={toggleMute}
              className={`rounded-full hover:bg-gray-100 transition-colors flex items-center justify-center p-2`}
              title={isMuted ? "Unmute audio" : "Mute audio"}
              style={{
              }}
            >
              {isMuted ?
                <VolumeX className="w-6 h-6" style={{ color: colors.themeColor }} /> :
                <Volume2 className="w-6 h-6" style={{ color: colors.themeColor }} />
              }
            </button>
            <button className="flex flex-col items-center">
              <div ref={titleRef} className="flex flex-col px-5" onClick={() => {
                report(ReportEvent.HEADER_CHAPTER_TITLE_CLICK, {
                });
                modeType !== ModeType.MISTAKE && setShowAllChapters(true);
              }}>
                <div className="flex flex-row items-center gap-2 text-lg font-bold">
                  {
                    cover_image && deviceType !== 'tablet' && !hideCoverImage && (
                      <div className='flex flex-row w-12 h-fit items-end'>
                        <Image src={cover_image!} alt={`${chapter_title}-cover-image`} width={35} height={35} className="object-cover" optimizedWidth={80} />
                        {schedule && <Image className="w-fit h-5 object-cover rounded-full -ml-3" src={schedule?.type === "custom" ? "/assets/schedule-custom-round.png" : "/assets/schedule-school-round.png"} alt="quest" width={100} height={100} />}
                      </div>
                    )
                  }
                  <div className="flex flex-col">
                    {!hideChapterTitle && deviceType !== 'tablet' && <span className="text-sm emphasize-text" style={{ color: colors.mainTitleColor }}>{chapter_title}</span>}
                    <span className="emphasize-text line-clamp-2 md:text-lg text-sm" style={{ color: colors.mainTitleColor }}>{lesson_title}</span>
                  </div>
                  {!hideTableOfContents && modeType !== ModeType.MISTAKE && <div className="flex flex-row items-center gap-2">
                    <ChevronDown color={colors.mainTextColor} />
                  </div>}
                </div>
              </div>
            </button>
          </div>
          <div className={`flex flex-row justify-center items-center ${deviceType === 'tablet' ? '' : 'mt-2'}`}>
            {
              animatedBar ? (
                <AnimatedBar
                  progress={currentProgress}
                  className="w-3/4 md:w-1/2"
                  comboCount={comboCount}
                />
              ) : (
                <Bar
                  progress={currentProgress}
                  className="w-3/4 md:w-1/2"
                />
              )
            }
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full" style={{ height: betterExperienceTip ? `${baseHeight + 30}px` : `${baseHeight}px` }}>
      <div className={`w-full max-w-[100vw] relative h-[${baseHeight}px] mx-auto flex flex-row items-center justify-center bg-white text-[#735648] overflow-x-hidden overflow-y-visible`} style={{ borderBottom: '1px solid #E5E5E5' }}>
        <div className="learn-main-container flex flex-row items-center justify-between overflow-x-hidden overflow-y-visible">
          <div className={`flex items-center justify-center h-full gap-4`}>
            <button onClick={handleBack} className={`hover:bg-gray-100 transition-colors rounded-full p-2 cursor-pointer`}>
              <Image src="/assets/ic_home.png" alt="back" width={20} height={20} className="object-cover" optimizedWidth={80} />
            </button>
            {!hideTableOfContents && modeType !== ModeType.MISTAKE && <button onClick={() => setShowAllChapters(true)} className={`hover:bg-gray-100 transition-colors rounded-full p-2 cursor-pointer`}>
              <Image src="/assets/ic_toc.png" alt="table of contents" width={20} height={20} className="object-cover" optimizedWidth={80} />
            </button>}
          </div>

          {renderBookTitle()}

          <div className={`flex md:flex-row flex-col items-start gap-2 md:gap-5 px-0 md:px-4`}>
            <div className="flex flex-row items-center cursor-pointer" onClick={() => useRewardStore.setState({ diamondPopup: true })}>
              <Image src="/assets/diamond-3.png" ref={totalDiamondImageRef} alt="coin" width={30} height={30} className="h-[25px] md:h-[30px] w-auto object-contain" optimizedWidth={80} />
              <div className="text-xl md:text-2xl ml-1 md:ml-2 emphasize-text" style={{ color: colors.mainTitleColor }}>{diamondCount}</div>
              {showDemoGems && (
                <span className="ml-2 rounded-full bg-[#FFF4D6] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#A16207]">
                  Demo
                </span>
              )}
            </div>
            {
              showHeart && (
                <Suspense fallback={null}>
                  <div className="flex flex-row items-center">
                    <div className="flex w-[60px] text-2xl" onClick={() => useRewardStore.setState({ buyHeartPopup: true })}>
                      <HeartAnimation className="w-[30px] h-[30px] md:w-[35px] md:h-[30px]" />
                    </div>
                  </div>
                </Suspense>
              )
            }
          </div>
        </div>
      </div>
      <Combo comboCount={comboCount} />
      {
        betterExperienceTip && (
          <div className="w-full h-[30px] flex flex-row items-center justify-between px-4" style={{ backgroundColor: colors.secondaryColor }}>
            <div className="flex flex-row items-center gap-2">
              <Image src="/assets/landing-logo.png" alt="landing-logo" width={40} height={40} className="object-cover h-[25px] w-auto" />
              <span className="display-text text-base text-white">Use a PC or Tablet for better experience</span>
            </div>
            <button className="flex flex-row items-center gap-2" onClick={closeBetterExperienceTip}>
              <X color="#ffffff" />
            </button>
          </div>
        )
      }
      <Suspense fallback={null}>
        {!hideTableOfContents ? (
          <QuestPracticePopup
            quest={quest}
            visible={showAllChapters}
            userId={user?.id || ''}
            onAuthRequired={onAuthRequired}
            from={'quest'}
            onClose={() => setShowAllChapters(false)}
            isTextbookMode={modeType === ModeType.PROBLEM_SET}
            previewOnly={previewOnly}
            initialChapterId={initialChapterId}
            initialLessonId={initialLessonId}
            allowAssignActions={allowAssignActions}
          />
        ) : null}
      </Suspense>
      <DiamondPopup visible={diamondPopup} onClose={() => useRewardStore.setState({ diamondPopup: false })} />
      {showLeavingModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/45">
          <Loader2 className="h-10 w-10 animate-spin text-white" />
        </div>
      )}
    </div>
  )
}

export default Header;
